import React from 'react'

import {
  DeliveryForecastDespatchAdvice,
  PatchRequest,
  WebEdiDeliveryForecastDto,
} from '../../../../data/models'
import { useConfigContext } from '../../../ui-config'
import {
  requestPatchDeliveryForecastDespatchAdviceById,
  useDeliveryForecastDespatchAdviceById,
} from '../../data'
import {
  DataForConfirmationDto,
  DataForConfirmationPatchDto,
} from '../../data/model'
import { DataForDeliveryForecastDespatchAdviceForm } from './DataForDeliveryForecastDespatchAdvice.form'

type Props = {
  despatchAdviceId: string
}

export const DataForDeliveryForecastDespatchAdviceData: React.FC<Props> = ({
  despatchAdviceId,
}) => {
  const { uiConfig: config } = useConfigContext()
  const { data, isLoading, mutate } =
    useDeliveryForecastDespatchAdviceById(despatchAdviceId)

  const getDisabledFieldIndexByName = (name: string): number => {
    const foundIndex = (
      data?.Payload.Meta?.Fields?.DisabledCalculation || []
    ).findIndex(p => p.Fieldname === name)

    return foundIndex
  }

  const getDisabledFieldValueByName = (name: string): boolean => {
    const foundIndex = getDisabledFieldIndexByName(name)

    if (foundIndex < 0) return false

    return (
      (data?.Payload.Meta?.Fields?.DisabledCalculation || [])[foundIndex]
        .IsDisabled || false
    )
  }

  const prepareMetaPatchOperation = (
    fieldsToCheck: { name: string; value: boolean }[],
  ) => {
    const values = [...(data?.Payload.Meta?.Fields?.DisabledCalculation || [])]

    fieldsToCheck.forEach(f => {
      const foundIndex = values.findIndex(p => p.Fieldname === f.name)
      if (foundIndex < 0) {
        values.push({ Fieldname: f.name, IsDisabled: f.value })
      } else {
        values[foundIndex].IsDisabled = f.value
      }
    })

    return values
  }

  const dataToFormValues = (
    data?:
      | WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
      | undefined,
  ): DataForConfirmationDto => {
    return {
      deliveryNoteNumber: data?.Payload.DeliveryNoteNumber || '',
      deliveryDate: data?.Payload.ConfirmationDeliveryDate || '',
      deliveryType: data?.Payload.DeliveryType || 'FULL',
      confirmationCode: data?.Payload.ConfirmationCode || 'NOT_SET',
      confirmationId: data?.Payload.ConfirmationId || '',
      confirmationDate: data?.Payload.ConfirmationDate || '',
      termsOfDeliveryCode: data?.Payload.TermsOfDelivery?.Code,
      confirmationGrossWeight: data?.Payload.ConfirmationGrossWeight || 0,
      confirmationGrossWeightChecked: getDisabledFieldValueByName(
        'ConfirmationGrossWeight',
      ),
      confirmationGrossWeightUnit:
        data?.Payload.ConfirmationGrossWeightUnit || '',
      confirmationNumberOfUnits: data?.Payload.ConfirmationNumberOfUnits || 0,
      confirmationNumberOfUnitsChecked: getDisabledFieldValueByName(
        'ConfirmationNumberOfUnits',
      ),
      plant: data?.Payload.Plant || '',
      unloadingPoint: data?.Payload.UnloadingPoint || '',
      gln: data?.Payload.CarrierInformation?.GLN,
      company: data?.Payload.CarrierInformation?.Company || '',
      department: data?.Payload.SellerInformation.Department || '',
      name: data?.Payload.SellerInformation.Contact?.Name || '',
      phone: data?.Payload.SellerInformation.Contact?.Phone || '',
      email: data?.Payload.SellerInformation.Contact?.Email || '',
    }
  }

  const patchFromFormValues = (
    formData: DataForConfirmationPatchDto,
  ): PatchRequest<
    WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
  > => {
    const patchOperations: PatchRequest<
      WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
    > = []

    // deliveryNoteNumber
    if (formData.deliveryNoteNumber !== data?.Payload.DeliveryNoteNumber) {
      patchOperations.push({
        op: data?.Payload.DeliveryNoteNumber ? 'replace' : 'add',
        path: '/Payload/DeliveryNoteNumber',
        value: formData.deliveryNoteNumber,
      })
    }

    // deliveryDate
    if (formData.deliveryDate !== data?.Payload.ConfirmationDeliveryDate) {
      patchOperations.push({
        op: data?.Payload.ConfirmationDeliveryDate ? 'replace' : 'add',
        path: '/Payload/ConfirmationDeliveryDate',
        value: formData.deliveryDate,
      })
    }

    // termsOfDeliveryCode
    if (formData.termsOfDeliveryCode !== data?.Payload.TermsOfDelivery?.Code) {
      patchOperations.push({
        op: data?.Payload.TermsOfDelivery?.Code ? 'replace' : 'add',
        path: '/Payload/TermsOfDelivery/Code',
        value: formData.termsOfDeliveryCode,
      })
    }

    // confirmationGrossWeight
    if (
      formData.confirmationGrossWeight !== data?.Payload.ConfirmationGrossWeight
    ) {
      patchOperations.push({
        op: data?.Payload.ConfirmationGrossWeight ? 'replace' : 'add',
        path: '/Payload/ConfirmationGrossWeight',
        value: formData.confirmationGrossWeight,
      })
    }

    // confirmationGrossWeightChecked
    // if (
    //   formData.confirmationGrossWeightChecked !==
    //   getDisabledFieldValueByName('ConfirmationGrossWeight')
    // ) {
    //   patchOperations.push({
    //     op: data?.Payload.Meta?.Fields?.DisabledCalculation ? 'replace' : 'add',
    //     path: '/Payload/Meta/Fields/DisabledCalculation',
    //     value: replaceDisabledFields(
    //       'ConfirmationGrossWeight',
    //       formData.confirmationGrossWeightChecked,
    //     ),
    //   })
    // }

    // confirmationGrossWeightUnit
    if (
      formData.confirmationGrossWeightUnit !==
      data?.Payload.ConfirmationGrossWeightUnit
    ) {
      patchOperations.push({
        op: data?.Payload.ConfirmationGrossWeightUnit ? 'replace' : 'add',
        path: '/Payload/ConfirmationGrossWeightUnit',
        value: formData.confirmationGrossWeightUnit,
      })
    }

    // confirmationNumberOfUnits
    if (
      formData.confirmationNumberOfUnits !==
      data?.Payload.ConfirmationNumberOfUnits
    ) {
      patchOperations.push({
        op: data?.Payload.ConfirmationNumberOfUnits ? 'replace' : 'add',
        path: '/Payload/ConfirmationNumberOfUnits',
        value: formData.confirmationNumberOfUnits,
      })
    }

    // confirmationNumberOfUnitsChecked
    // if (
    //   formData.confirmationNumberOfUnitsChecked !==
    //   getDisabledFieldValueByName('ConfirmationNumberOfUnits')
    // ) {
    //   patchOperations.push({
    //     op: data?.Payload.Meta?.Fields?.DisabledCalculation ? 'replace' : 'add',
    //     path: '/Payload/Meta/Fields/DisabledCalculation',
    //     value: replaceDisabledFields(
    //       'ConfirmationNumberOfUnits',
    //       formData.confirmationNumberOfUnitsChecked,
    //     ),
    //   })
    // }

    // company
    if (formData.company !== data?.Payload.CarrierInformation?.Company) {
      patchOperations.push({
        op: data?.Payload.CarrierInformation?.Company ? 'replace' : 'add',
        path: '/Payload/CarrierInformation/Company',
        value: formData.company,
      })
    }

    // gln
    if (formData.gln !== data?.Payload.CarrierInformation?.GLN) {
      patchOperations.push({
        op: data?.Payload.CarrierInformation?.GLN ? 'replace' : 'add',
        path: '/Payload/CarrierInformation/GLN',
        value: formData.gln,
      })
    }

    // department
    if (formData.department !== data?.Payload.SellerInformation.Department) {
      patchOperations.push({
        op: data?.Payload.SellerInformation.Department ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Department',
        value: formData.department,
      })
    }

    // name
    if (formData.name !== data?.Payload.SellerInformation.Contact?.Name) {
      patchOperations.push({
        op: data?.Payload.SellerInformation.Contact?.Name ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Name',
        value: formData.name,
      })
    }

    // phone
    if (formData.phone !== data?.Payload.SellerInformation.Contact?.Phone) {
      patchOperations.push({
        op: data?.Payload.SellerInformation.Contact?.Phone ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Phone',
        value: formData.phone,
      })
    }

    // email
    if (formData.email !== data?.Payload.SellerInformation.Contact?.Email) {
      patchOperations.push({
        op: data?.Payload.SellerInformation.Contact?.Email ? 'replace' : 'add',
        path: '/Payload/SellerInformation/Contact/Email',
        value: formData.email,
      })
    }

    // Meta Object
    if (
      formData.confirmationNumberOfUnitsChecked !==
        getDisabledFieldValueByName('ConfirmationNumberOfUnits') ||
      formData.confirmationGrossWeightChecked !==
        getDisabledFieldValueByName('ConfirmationGrossWeight')
    ) {
      const newValues = prepareMetaPatchOperation([
        {
          name: 'ConfirmationGrossWeight',
          value: formData.confirmationGrossWeightChecked,
        },
        {
          name: 'ConfirmationNumberOfUnits',
          value: formData.confirmationNumberOfUnitsChecked,
        },
      ])

      patchOperations.push({
        op: data?.Payload.Meta?.Fields?.DisabledCalculation ? 'replace' : 'add',
        path: '/Payload/Meta/Fields/DisabledCalculation',
        value: newValues,
      })
    }

    return patchOperations
  }

  const onFormSubmit = async (formData: DataForConfirmationPatchDto) => {
    const patchData: PatchRequest<
      WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>
    > = patchFromFormValues(formData)
    await requestPatchDeliveryForecastDespatchAdviceById(
      despatchAdviceId,
      patchData,
    )
    mutate()
  }

  return (
    <>
      <DataForDeliveryForecastDespatchAdviceForm
        data={dataToFormValues(data)}
        config={config}
        onSubmit={onFormSubmit}
        isLoading={isLoading}
      />
    </>
  )
}
